/**
 * Depósitos: cuándo llega el dinero al banco.
 *
 * El panel sabía responder «cuánto vendí» —Cobros, el widget de ventas, los
 * totales por oferta— y no sabía responder «cuándo me llega», que es la otra
 * mitad y la que le importa a quien cobra. Para averiguarlo había que salir a
 * Stripe, que es tanto como decir que esa mitad del producto no estaba aquí.
 *
 * Es sección propia y no una tarjeta dentro de Ajustes › Stripe porque no es un
 * ajuste: no se toca una vez y se olvida, se consulta. Vive al lado de Cobros
 * por la misma razón por la que Cobros vive al lado de Ofertas — lo vendido y
 * lo cobrado se miran por separado.
 *
 * Lo que se enseña es el componente incrustado de Connect: saldo disponible,
 * saldo en camino, el calendario de depósitos de la cuenta y el historial, con
 * el botón de retirar cuando la cuenta lo permite. Reimplementarlo habría sido
 * escribir el calendario de liquidación de Stripe por país, que cambia sin
 * avisarnos.
 */
import { useCallback, useEffect, useState } from 'react'
import { SectionBar } from '../SectionBar'
import { ConnectPayouts } from '@stripe/react-connect-js'
import { SpinningMascot } from '../../RecordLoader'
import { StripeEmbedProvider, EmbedLoading, embedErrorMessage, isEmbeddedStripeConfigured } from './StripeEmbed'
import { StripeConnectBanner } from './StripeConnectBanner'
import { getStripeBalance, getStripeConnection, listOrders, type ConnectStatus, type StripeBalance } from '../../../lib/api'
import { failureDetail, formatMoney, payoutArrival } from './offerShared'
import { useI18n } from '../../../lib/i18n'
import { IconBuildingBank, IconExternalLink } from '@tabler/icons-react'

export function PayoutsSection({ projectId, token, canConnectStripe = true, onOpenStripeSettings, onOpenPlans }: {
  projectId: string
  token: string
  /** Si el plan da para conectar. Ver `assertCanConnectStripe` en el worker. */
  canConnectStripe?: boolean
  /** A donde lleva el aviso cuando falta cuenta o falta terminar el alta. */
  onOpenStripeSettings: () => void
  /** Y a donde lleva cuando lo que falta es el plan. */
  onOpenPlans?: (() => void) | undefined
}) {
  const { t } = useI18n()
  /** `null` mientras no se sabe: el aviso no afirma nada hasta que llega. */
  const [status, setStatus] = useState<ConnectStatus | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [embedError, setEmbedError] = useState('')
  /* Sin ventas no hay nada que depositar, y el componente de Stripe lo cuenta
     con un cuadro de ceros que no explica por qué está vacío. `null` mientras
     no se sabe: hasta que llega la respuesta no se enseña ni una cosa ni otra,
     porque afirmar «no hay ventas» antes de mirarlas sería mentir medio
     segundo. Si la consulta falla se deja pasar el embed: el saldo es de
     Stripe, y un fallo nuestro contando cobros no es motivo para esconderlo. */
  const [hasSales, setHasSales] = useState<boolean | null>(null)
  /* Las cifras, en tarjetas nuestras y encima del embed. El componente de Stripe
     ya las dice, pero dentro de un iframe al que sólo se le pueden pasar cuatro
     colores: nunca va a leerse como una sección del panel. Estas tres tarjetas
     son el mismo lenguaje que el resto —superficie apagada, borde de un pelo,
     rótulo arriba y cifra grande— y el embed se queda con lo que sólo él tiene:
     el historial, el calendario real y el botón de retirar.

     Sale del mismo endpoint que el widget de inicio a propósito: si cada
     pantalla preguntara a su manera, volverían a discrepar. */
  const [balance, setBalance] = useState<StripeBalance | null>(null)

  /**
   * Las tres consultas, a la vez.
   *
   * Iban en serie —una `await` detrás de otra— y la pantalla tardaba lo que
   * suman las tres, cuando ninguna necesita el resultado de las otras. La del
   * estado de la cuenta es además la más lenta: el servidor consulta Stripe
   * para poner al día una cuenta que todavía no está lista. En paralelo, la
   * espera pasa de ser la suma a ser la más lenta.
   *
   * Cada una conserva su propio manejo del fallo. Que no se pueda contar los
   * cobros no puede esconder el saldo, ni al revés: por eso siguen siendo tres
   * `try` y no uno solo alrededor de todo.
   */
  const load = useCallback(async () => {
    const connection = getStripeConnection(projectId, token)
      .then((result) => { setStatus(result.status); setFailure(null) })
      .catch((error: unknown) => { setFailure(failureDetail(error)) })

    /* El saldo, en silencio si falla: las tarjetas desaparecen y queda el embed,
       que trae los mismos números de su propia fuente. Un error nuestro encima
       de una pantalla que sí funciona sólo alarmaría. */
    const balanceCall = getStripeBalance(projectId, token)
      .then(setBalance)
      .catch(() => { setBalance(null) })

    const salesCall = listOrders(projectId, token)
      .then((orders) => { setHasSales(orders.length > 0) })
      .catch(() => { setHasSales(true) })

    await Promise.allSettled([connection, balanceCall, salesCall])
  }, [projectId, token])

  useEffect(() => { void load() }, [load])

  /* Con la cuenta a medias el componente de Stripe no tiene saldo que enseñar y
     carga un cuadro vacío. El aviso dice qué falta y lleva donde se arregla. */
  const connected = status === 'ready' || status === 'restricted'
  /* En gratis el aviso de arriba ya es la oferta entera. El hueco de «todavía
     no hay depósitos» debajo repetiría en gris lo que la tarjeta acaba de decir
     en color, y encima como si el problema fuera no tener ventas. */
  const planLocked = !failure && status === 'none' && !canConnectStripe

  return (
    <div className="dash-payouts-view">
      <SectionBar
        icon={<IconBuildingBank size={17} />}
        title={t('payouts.title')}
        subtitle={t('payouts.subtitle')}
      >
        {connected ? (
          /* Enlace y no botón: la URL es fija, así que no hay nada que pedirle
             al servidor antes de abrirla y el navegador no lo bloquea. */
          <a className="dash-secondary-btn" href="https://dashboard.stripe.com/payouts" target="_blank" rel="noreferrer">
            <IconExternalLink size={14} />
            {t('payouts.openStripe')}
          </a>
        ) : null}
      </SectionBar>

      {/* El único que entra con animación. Aquí no se puede esperar a que todo
          esté listo antes de pintar —debajo hay un marco de Stripe que monta
          cuando le parece— así que el aviso sí llega de golpe sobre algo ya
          dibujado, y abrir su altura evita el tirón. */}
      <StripeConnectBanner
        reveal
        status={status}
        failed={Boolean(failure)}
        detail={failure ?? undefined}
        canConnect={canConnectStripe}
        onConnect={onOpenStripeSettings}
        onOpenPlans={onOpenPlans}
        onRetry={() => { void load() }}
      />

      {connected && balance && hasSales !== false ? (
        <div className="dash-payout-cards">
          {/* Primera y más ancha: es la pregunta por la que se entra aquí. La
              cifra es el depósito real si Stripe ya lo creó y el saldo entero
              —disponible más en camino— mientras no exista, que es lo mismo que
              Stripe llama «saldo total» dos centímetros más abajo. */}
          <div className="dash-payout-card is-lead">
            <span className="dash-payout-card-label">
              {t(balance.nextPayout ? 'payouts.next' : 'payouts.balance')}
            </span>
            <strong>
              {formatMoney(balance.nextPayout ? balance.nextPayout.amount : balance.available + balance.pending, balance.currency)}
            </strong>
            <span className="dash-payout-card-meta">
              {!balance.payoutsEnabled
                ? t('payouts.notEnabled')
                : balance.nextPayout
                  /* Fecha de Stripe: es un hecho, no lleva «aprox.». */
                  ? `${t('payouts.arrives', { when: payoutArrival(balance.nextPayout.arrivalDate, t) })}${balance.nextPayout.status === 'in_transit' ? t('payouts.inTransitSuffix') : ''}`
                  : balance.estimatedArrival
                    /* La nuestra sí: sale del calendario de la cuenta y no
                       conoce los festivos que Stripe sí descuenta. */
                    ? t('payouts.arrivesApprox', { when: payoutArrival(balance.estimatedArrival, t) })
                    : t('payouts.noneScheduled')}
            </span>
          </div>

          <div className="dash-payout-card">
            <span className="dash-payout-card-label">{t('payouts.available')}</span>
            <strong>{formatMoney(balance.available, balance.currency)}</strong>
            <span className="dash-payout-card-meta">
              {/* Un disponible en negativo no es un error de cuentas: son
                  reembolsos y disputas ya cobrados que se descuentan de lo que
                  venga. Dicho aquí, donde está el número que extraña. */}
              {t(balance.available < 0 ? 'payouts.availableNegative' : 'payouts.availableReady')}
            </span>
          </div>

          <div className="dash-payout-card">
            <span className="dash-payout-card-label">{t('payouts.inTransit')}</span>
            <strong>{formatMoney(balance.pending, balance.currency)}</strong>
            <span className="dash-payout-card-meta">{t('payouts.inTransitNote')}</span>
          </div>
        </div>
      ) : null}

      {connected && !isEmbeddedStripeConfigured() ? (
        <p className="dash-card-description">
          {t('payouts.viaStripeOnly')}
        </p>
      ) : connected && hasSales === false ? (
        <div className="dash-placeholder">
          <IconBuildingBank size={34} aria-hidden="true" />
          <strong>{t('payouts.empty.title')}</strong>
          <span>{t('payouts.empty.body')}</span>
        </div>
      ) : connected ? (
        /* La tarjeta envuelve también el «cargando» y el error, y no sólo el
           embed: si el marco apareciera con el iframe, la sección arrancaría
           siendo un texto suelto y daría un salto al llegar Stripe. */
        <div className="dash-stripe-embed-card">
          {hasSales === null ? (
            /* Todavía contando ventas: se espera aquí en vez de montar el embed
               y quitarlo, que sería enseñar un saldo para retirarlo enseguida. */
            <EmbedLoading label={t('payouts.loadingBalance')} />
          ) : embedError ? (
            <p className="dash-order-detail-note">{embedError}</p>
          ) : (
            <div className="dash-stripe-inline-embed">
              <StripeEmbedProvider
                projectId={projectId}
                token={token}
                surface="inline"
                fallback={<EmbedLoading label={t('payouts.loadingBalance')} />}
                onFail={setEmbedError}
              >
                <ConnectPayouts
                  onLoadError={(event) => {
                    console.error('[depósitos] Stripe no cargó los depósitos', event.error)
                    setEmbedError(embedErrorMessage(event.error, 'stripe.embed.subject.payouts', t))
                  }}
                />
              </StripeEmbedProvider>
            </div>
          )}
        </div>
      ) : failure || planLocked ? null : status === null ? (
        /* Ese segundo que tarda la consulta del estado no puede quedar en
           blanco: el hueco guarda su sitio mientras se sabe, y lo que cambia
           después es el texto, no la altura. */
        <div className="dash-placeholder dash-list-loading" role="status" aria-busy="true">
          <SpinningMascot spinning size={44} />
          <span>{t('payouts.loadingAccount')}</span>
        </div>
      ) : (
        /* Sin cuenta conectada la pantalla se quedaba en el aviso y un vacío:
           todo lo de arriba cuelga de `connected`. El hueco dice qué se verá
           aquí, para que la sección se explique aunque todavía no tenga nada
           que enseñar. Sin botón: el del aviso ya lleva al mismo sitio, y dos
           llamadas a la misma acción hacen dudar sobre si son la misma. */
        <div className="dash-placeholder">
          <IconBuildingBank size={34} aria-hidden="true" />
          <strong>{t('payouts.empty.title')}</strong>
          <span>{t('payouts.emptyNoAccount.body')}</span>
        </div>
      )}
    </div>
  )
}
